import React, { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  LoaderCircle,
  RefreshCw,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

export default function CounselPracticeContentAudit() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(unwrap(await api.get('/counsel-practice/admin/content-audit', { __silent: true })))
    } catch (e) {
      setError(getApiErrorMessage(e, '콘텐츠 점검 결과를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return <div className="flex min-h-56 items-center justify-center text-slate-500"><LoaderCircle className="h-6 w-6 animate-spin" aria-label="콘텐츠 점검 중" /></div>
  }

  const issues = Array.isArray(data?.issues) ? data.issues : []
  const passed = Number(data?.errorCount) === 0 && Number(data?.warningCount) === 0

  return (
    <section className="mt-8 border-t border-slate-200 pt-6" aria-labelledby="content-audit-title" data-testid="counsel-content-audit">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-teal-700" aria-hidden="true" />
            <h2 id="content-audit-title" className="text-lg font-semibold text-slate-950">콘텐츠 점검</h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">사용 중인 시나리오와 미션에서 빠진 내용이나 겹치는 질문이 있는지 확인합니다.</p>
        </div>
        <button type="button" onClick={load} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50" title="다시 점검" aria-label="콘텐츠 다시 점검"><RefreshCw className="h-4 w-4" aria-hidden="true" /></button>
      </div>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}

      <div className="mt-5 grid overflow-hidden border-y border-slate-200 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        {[
          ['점검 결과', data?.statusLabel || '확인 필요'],
          ['품질 점수', `${Number(data?.qualityScore) || 0}점`],
          ['점검한 콘텐츠', `${Number(data?.checkedCount) || 0}개`],
          ['5분 상담', `${Number(data?.scenarioCount) || 0}개`],
          ['1분 미션', `${Number(data?.missionCount) || 0}개`],
          ['답변 근거 연결', `${Number(data?.groundedScenarioCount) || 0}/${Number(data?.scenarioCount) || 0}개 · ${Number(data?.groundingRate) || 0}%`],
          ['수정·확인', `${Number(data?.errorCount) || 0}건 · ${Number(data?.warningCount) || 0}건`],
        ].map(([label, value]) => <div key={label} className="px-4 py-4"><div className="text-xs font-medium text-slate-500">{label}</div><div className="mt-1 text-base font-semibold text-slate-900">{value}</div></div>)}
      </div>

      {passed ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />현재 사용 중인 콘텐츠가 점검 기준을 통과했습니다.</div>
      ) : (
        <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
          {issues.map((issue, index) => (
            <div key={`${issue.contentTitle}-${issue.fieldLabel}-${index}`} className="flex gap-3 py-4">
              <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${issue.severity === 'ERROR' ? 'text-rose-600' : 'text-amber-600'}`} aria-hidden="true" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-xs font-semibold text-slate-500">{issue.severityLabel}</span>
                  <span className="text-xs text-slate-500">{issue.contentTypeLabel}</span>
                  <span className="text-sm font-semibold text-slate-900">{issue.contentTitle}</span>
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-700"><span className="font-medium">{issue.fieldLabel}:</span> {issue.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
